import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, User as UserIcon, Mail, Save, ShieldCheck, ShieldAlert, Upload, Clock, XCircle } from 'lucide-react';
import L from 'leaflet';

// Fix Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function LocationMarker({ position, setPosition }) {
  useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });
  return position ? <Marker position={position} /> : null;
}

const Profile = () => {
  const { user, setUser } = React.useContext(AuthContext);
  const [name, setName] = useState(user?.name || '');
  const [position, setPosition] = useState(user?.location ? [user.location.lat, user.location.lng] : null);
  const [address, setAddress] = useState(user?.location?.address || '');
  const [loading, setLoading] = useState(false);
  const [idUploading, setIdUploading] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const isVerified = user?.is_verified || user?.is_admin;
  const verificationStatus = user?.is_admin ? 'verified' : (user?.verification_status || 'unverified');

  const fetchAddress = async (lat, lng) => {
    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      setAddress(response.data.display_name);
    } catch (error) {
      setAddress(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
    }
  };

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          setPosition([lat, lng]);
          fetchAddress(lat, lng);
          toast.success('Current location detected');
        },
        () => { toast.error('Could not get current location'); }
      );
    } else {
      toast.error('Geolocation is not supported by your browser');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        name,
        location: position ? { lat: position[0], lng: position[1], address } : null
      };
      await axios.put(`${API}/users/profile`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser({ ...user, ...updateData });
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleIdUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file (JPG, PNG, etc.)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setIdUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('id_image', file);

      await axios.post(`${API}/users/upload-id`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setUser({ ...user, verification_status: 'pending' });
      toast.success('ID uploaded! Your verification is pending admin review.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload ID');
    } finally {
      setIdUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-emerald-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div
                style={{ backgroundColor: 'rgb(26, 160, 157)' }}
                className="px-6 py-2.5 rounded-lg shadow-md cursor-pointer"
                onClick={() => navigate('/dashboard')}
              >
                <h1 className="text-xl font-bold text-white logo-text whitespace-nowrap">STRAY SPOTTER</h1>
              </div>
            </div>
            <Button onClick={() => navigate('/dashboard')} variant="ghost" data-testid="back-btn" className="hover:bg-teal-50">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-4xl font-bold mb-8 text-gray-900" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>My Profile</h1>

        <div className="space-y-6">
          {/* Identity Verification Card */}
          <Card className="shadow-lg border-0" data-testid="verification-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {isVerified ? (
                  <ShieldCheck className="w-5 h-5 text-green-600" />
                ) : (
                  <ShieldAlert className="w-5 h-5 text-amber-500" />
                )}
                Identity Verification
              </CardTitle>
              <CardDescription>
                Verify your identity to unlock animal locations on posts
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isVerified ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3" data-testid="verified-status">
                  <ShieldCheck className="w-8 h-8 text-green-600 flex-shrink-0" />
                  <div>
                    <p className="font-bold text-green-800">Identity Verified</p>
                    <p className="text-sm text-green-600">You have full access to animal locations on all posts.</p>
                  </div>
                </div>
              ) : verificationStatus === 'pending' ? (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3" data-testid="pending-status">
                  <Clock className="w-8 h-8 text-amber-600 flex-shrink-0" />
                  <div>
                    <p className="font-bold text-amber-800">Verification Pending</p>
                    <p className="text-sm text-amber-600">Your ID has been submitted and is awaiting admin review. You'll get access to animal locations once approved.</p>
                  </div>
                </div>
              ) : verificationStatus === 'rejected' ? (
                <div className="space-y-4">
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3" data-testid="rejected-status">
                    <XCircle className="w-8 h-8 text-red-600 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-red-800">Verification Rejected</p>
                      <p className="text-sm text-red-600">Your ID was not approved. Please ensure the name on your ID matches your profile name, then try again.</p>
                    </div>
                  </div>
                  <input type="file" ref={fileInputRef} onChange={handleIdUpload} accept="image/*" className="hidden" />
                  <Button onClick={() => fileInputRef.current?.click()} disabled={idUploading} data-testid="reupload-id-btn" className="text-white" style={{ backgroundColor: 'rgb(26, 160, 157)' }}>
                    {idUploading ? (
                      <div className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>Uploading...</div>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" /> Re-upload ID</>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-amber-50 border-2 border-dashed border-amber-300 rounded-lg" data-testid="unverified-status">
                    <div className="flex items-start gap-3">
                      <ShieldAlert className="w-6 h-6 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-amber-800 mb-1">Verification Required</p>
                        <p className="text-sm text-amber-700 mb-2">
                          To protect animals from potential harm, exact locations are hidden until your identity is verified.
                        </p>
                        <p className="text-sm text-amber-700">
                          Upload a photo of a valid ID (passport, driver's license, national ID). The <strong>name on the ID must match your profile name</strong>.
                        </p>
                      </div>
                    </div>
                  </div>
                  <input type="file" ref={fileInputRef} onChange={handleIdUpload} accept="image/*" className="hidden" />
                  <Button onClick={() => fileInputRef.current?.click()} disabled={idUploading} data-testid="upload-id-btn" className="w-full py-5 text-white" style={{ backgroundColor: 'rgb(26, 160, 157)' }}>
                    {idUploading ? (
                      <div className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>Uploading...</div>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" /> Upload Your ID to Get Verified</>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Profile Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name"><UserIcon className="w-4 h-4 inline mr-2" />Full Name</Label>
                  <Input id="name" data-testid="name-input" value={name} onChange={(e) => setName(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email"><Mail className="w-4 h-4 inline mr-2" />Email Address</Label>
                  <Input id="email" data-testid="email-input" value={user?.email} disabled className="bg-gray-50" />
                  <p className="text-xs text-gray-500">Email cannot be changed</p>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span><MapPin className="w-5 h-5 inline mr-2" style={{ color: 'rgb(26, 160, 157)' }} />My Location (for notifications)</span>
                  <Button type="button" onClick={handleUseCurrentLocation} data-testid="use-current-location-btn" variant="outline" size="sm" style={{ color: 'rgb(26, 160, 157)', borderColor: 'rgb(26, 160, 157)' }} className="hover:bg-teal-50">
                    Use Current Location
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="h-96 rounded-lg overflow-hidden" data-testid="location-map">
                  <MapContainer center={position || [51.505, -0.09]} zoom={position ? 13 : 2} className="h-full w-full">
                    <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <LocationMarker position={position} setPosition={setPosition} />
                  </MapContainer>
                </div>
                {position && (
                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(26, 160, 157, 0.1)' }}>
                    <p className="text-sm text-gray-700"><strong>Your Location:</strong> {address || 'Fetching address...'}</p>
                    <p className="text-xs text-gray-500 mt-1">Lat: {position[0].toFixed(6)}, Lng: {position[1].toFixed(6)}</p>
                  </div>
                )}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800"><strong>Note:</strong> Set your location to receive email notifications when animals are spotted within 50km of you.</p>
                </div>
              </CardContent>
            </Card>

            <Button type="submit" data-testid="save-profile-btn" style={{ backgroundColor: 'rgb(26, 160, 157)' }} className="w-full py-6 text-lg hover:opacity-90 text-white" disabled={loading}>
              {loading ? 'Saving...' : <><Save className="w-5 h-5 mr-2" />Save Profile</>}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;
